import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import Grid from "@material-ui/core/Grid";
import Slide from "@material-ui/core/Slide";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import UiButton from "../../common/UiButton";
import UiInput from "../../common/UiInput";
import UiSelect from "../../common/UiSelect";
// @ts-ignore
import CreatableSelect from "react-select/creatable";
import Tags from "./Tags";
import TagListSample from "./TagListSample";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import TagList from "./TagList";
import { withRouter } from "react-router-dom";
import withContext from "../../../tools/withContext";
import { fetchAvailableLabels } from "../../import/components/LabelAutocomplete";
import {
  LABELISATION_TYPES,
  getIdFromMax,
  sanitizeProjectName,
  sanitizeTextWithSeparators,
} from "./utils";
import { appPageRootMinHeight } from "../../appBar/appBarTheme";

const tagSelectStyles: any = {
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: 4,
    backgroundColor: "#fff",
    border: state.isFocused
      ? "1px solid #80bdff"
      : "1px solid rgba(72,72,72,.2)",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "none",
    fontSize: 16,
    minHeight: 42,
    cursor: "text",
    "&:hover": {
      border: state.isFocused
        ? "1px solid #80bdff"
        : "1px solid rgba(72,72,72,.4)",
    },
  }),
  input: (base: any) => ({
    ...base,
    fontSize: 16,
    margin: 0,
    padding: "2px 0",
  }),
  placeholder: (base: any) => ({ ...base, color: "#aaa", fontSize: 16 }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 16,
    backgroundColor: state.isFocused ? "rgba(0,123,255,.05)" : "white",
    color: "#333",
    cursor: "pointer",
    padding: "8px 12px",
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
    boxShadow: "0 2px 8px rgba(0,0,0,.15)",
    borderRadius: 4,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
  dropdownIndicator: () => ({ display: "none" }),
  indicatorSeparator: () => ({ display: "none" }),
  clearIndicator: (base: any) => ({
    ...base,
    padding: "0 8px",
    color: "#aaa",
    "&:hover": { color: "#666" },
  }),
};

interface TagInputProps {
  appFetch: (url: string, opts?: any) => Promise<any>;
  clientId?: string;
  labelingType?: string;
  onAdd: (name: string) => void;
  disabled?: boolean;
}

const TagInput = ({
  appFetch,
  clientId,
  labelingType,
  onAdd,
  disabled = false,
}: TagInputProps) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    hasFetched.current = false;
    setOptions([]);
    setInputValue("");
  }, [clientId, labelingType]);

  const loadLabels = useCallback(() => {
    if (disabled || hasFetched.current || !clientId || !labelingType) return;

    setIsLoading(true);
    fetchAvailableLabels(appFetch, clientId, labelingType)
      .then((labels) => {
        setOptions(labels);
        hasFetched.current = true;
      })
      .catch(() => setOptions([]))
      .finally(() => setIsLoading(false));
  }, [appFetch, clientId, labelingType, disabled]);

  const query = inputValue.trim().toLowerCase();
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query))
    : options;

  const handleSelect = (opt: any) => {
    if (!opt) return;
    onAdd(opt.value);
    setInputValue("");
  };

  return (
    <div>
      <label
        style={{
          fontWeight: 200,
          display: "block",
          fontSize: 16,
          color: disabled ? "silver" : undefined,
        }}
      >
        Labels
      </label>
      <div style={{ marginTop: 8 }}>
        <CreatableSelect
          inputValue={inputValue}
          value={null}
          options={filtered}
          isLoading={isLoading}
          isDisabled={disabled}
          onInputChange={(val: string, { action }: any) => {
            if (action === "input-change") setInputValue(val);
          }}
          onFocus={loadLabels}
          onChange={handleSelect}
          onCreateOption={(val: string) => {
            onAdd(val.trim());
            setInputValue("");
          }}
          formatCreateLabel={(input: string) => `Ajouter "${input}"`}
          styles={tagSelectStyles}
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : undefined
          }
          menuPosition="fixed"
          placeholder={
            disabled
              ? "Client et type requis..."
              : "Rentrez la liste des labels..."
          }
          noOptionsMessage={() => (isLoading ? null : "Aucun résultat")}
          loadingMessage={() => "Chargement..."}
          isClearable={false}
          filterOption={() => true}
        />
      </div>
    </div>
  );
};

const SwitchModeButton = ({
  label,
  labelisationTypeValue,
  classes,
  toggleTagListMode,
}: any) => {
  return (
    <>
      {(labelisationTypeValue === "RECTANGLE_BOXING" ||
        labelisationTypeValue === "CLASSIF") && (
        <div style={{ textAlign: "right" }}>
          <button className={classes.btnLink} onClick={toggleTagListMode}>
            {label}
          </button>
        </div>
      )}
    </>
  );
};

const pDefault = {
  name: "",
  labelingTypes: [
    {
      name: "RECTANGLE_BOXING",
      listTags: [],
    },
  ],
  dataSource: "S3",
  environment: "",
  indice: "",
  admins: [],
  tagListId: "",
  clientId: "",
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      ...appPageRootMinHeight(theme),
    },
    paper: {
      minWidth: "100%",
      borderRadius: "5px",
    },
    header: {
      backgroundColor: theme.palette.primary.main,
      padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
      borderRadius: "5px 5px 0 0",
    },
    progress: {
      width: 250,
      minHeight: 40,
      margin: `${theme.spacing.unit * 2}px ${theme.spacing.unit}px`,
    },
    title: {
      color: "white",
    },
    form: {
      padding: `${theme.spacing.unit * 4}px`,
    },
    margin: {
      margin: theme.spacing.unit,
      fontWeight: 200,
    },
    label: {
      fontWeight: 200,
      display: "block",
      fontSize: 16,
    },
    sourceDonnees: {
      marginTop: theme.spacing.unit,
    },
    btnLink: {
      padding: "5px 0",
      backgroundColor: "transparent",
      textDecoration: "none",
      border: "none",
      "&:hover": {
        textDecoration: "underline",
      },
      cursor: "pointer",
      marginRight: "-10px",
    },
    error: {
      color: "red",
      paddingTop: theme.spacing.unit,
    },
  });

type Props = {
  classes: any;
  clients: any;
  project: any;
  saveProject: any;
  checkProjectName: any;
  indices: any;
  tagLists: any;
  history: any;
  ctx: any;
};

const UpdateProject = ({
  classes,
  clients,
  saveProject,
  checkProjectName,
  indices,
  tagLists,
  project,
  history,
  ctx,
}: Props) => {
  const { appFetch } = ctx;
  const [datas, setDatas] = useState<any>(pDefault);
  const [error, setError] = useState<string>("");
  const [tagsList, setTagsList] = useState<any>({});
  const [originalTags, setOriginalTags] = useState<any>([]);
  const [tagDuplicated, setTagDuplicated] = useState<string | null>(null);
  const [tagListMode, setTagListMode] = useState<boolean>(false);

  const indice = indices.find((i: any) => i.name === datas.indice);
  const envs = indice
    ? indice.types[0].envs
        .filter((e: any) => e.name !== "@timestamp")
        .map((e: any) => e.name)
    : [];

  const handleChange = (e: any) => {
    const normalizedValue =
      e.target.name === "name" && typeof e.target.value === "string"
        ? sanitizeProjectName(e.target.value)
        : e.target.name === "currentTag" && typeof e.target.value === "string"
          ? sanitizeTextWithSeparators(e.target.value)
          : e.target.value;

    const value =
      e.target.name === "labelisationType"
        ? {
            labelingTypes: [
              {
                ...(datas.labelingTypes && datas.labelingTypes[0]),
                name: e.target.value,
                listTags:
                  e.target.value === "PLATE" || e.target.value === "PLATE_TEXT"
                    ? []
                    : (datas.labelingTypes &&
                        datas.labelingTypes[0] &&
                        datas.labelingTypes[0].listTags) ||
                      [],
              },
            ],
            ...(e.target.value === "VIDEO" ? { dataSource: "S3" } : {}),
          }
        : { [e.target.name]: normalizedValue };
    setDatas({ ...datas, ...value });
  };

  const handleBlur = (e: any) => {
    const trimmedName = (datas.name || "").trim();
    if (trimmedName !== datas.name) {
      setDatas({ ...datas, name: trimmedName });
    }

    if (trimmedName !== "" && typeof project.id === "undefined") {
      checkProjectName(trimmedName)
        .then(() => setError(""))
        .catch((e: any) => {
          setError("Ce nom de projet existe déjà");
        });
    } else {
      setError("");
    }
  };

  const addTagByName = (name: string) => {
    const normalizedTag = name.trim();
    if (normalizedTag === "") return;
    if (
      !datas.labelingTypes[0].listTags.find(
        (t: any) => t.name === normalizedTag,
      )
    ) {
      setTags(
        datas.labelingTypes[0].listTags.concat([
          {
            id: getIdFromMax(datas.labelingTypes[0].listTags),
            isNew: true,
            name: normalizedTag,
          },
        ]),
      );
    } else {
      setTagDuplicated(normalizedTag);
    }
  };

  const setTags = (listTags: any) => {
    setDatas({
      ...datas,
      currentTag: "",
      //@ts-ignore
      labelingTypes: [
        {
          ...datas.labelingTypes[0],
          listTags: listTags,
        },
      ],
    });
  };

  const removeTag = (tagId: any, tagName?: string) => {
    setTags(
      datas.labelingTypes[0].listTags.filter((t: any) =>
        typeof tagId !== "undefined" && tagId !== null
          ? t.id !== tagId
          : t.name !== tagName,
      ),
    );
  };

  const submit = async (e: any) => {
    e.preventDefault();
    const { currentTag, ...project } = datas;
    await saveProject(project)
      .then((res: any) => {
        if (res) {
          if (res) {
            setTimeout(
              () => history.push("/projects/0?sort=creationDate,desc"),
              2000,
            );
          }
        }
      })
      .catch((e: any) => {
        console.log("error", e);
      });
  };

  const isValidated = () => {
    const {
      name,
      labelingTypes,
      dataSource,
      indice,
      environment,
      tagListId,
      clientId,
    } = datas;

    return (
      name.trim() !== "" &&
      error === "" &&
      clientId !== "" &&
      labelingTypes &&
      labelingTypes[0] &&
      (labelingTypes[0].name.indexOf("PLATE") !== -1 ||
        labelingTypes[0].listTags.length > 0 ||
        tagListId !== "") &&
      (dataSource === "S3" || dataSource === "LABELS")
    );
  };

  const toggleTagListMode = (e: any) => {
    try {
      e.preventDefault();
      setDatas({ ...datas, tagListId: "" });
      setTagListMode(!tagListMode);
    } catch (e) {
      console.log("error", e);
    }
  };

  useEffect(() => {
    if (project.id) {
      const {
        name,
        labelingTypes,
        dataSource,
        esSourceConf,
        tagListId,
        client: { id: clientId },
      } = project;
      let environment = esSourceConf ? esSourceConf.esEnvironment : "";
      let indice = esSourceConf ? esSourceConf.esIndice : "";

      setDatas({
        name,
        labelingTypes,
        dataSource,
        environment,
        indice,
        tagListId,
        clientId,
      });
      setOriginalTags(project?.labelingTypes[0]?.listTags || []);
    }
  }, [project]);

  useEffect(() => {
    if (tagDuplicated !== null) {
      setTimeout(() => setTagDuplicated(null), 1000);
    }
  }, [tagDuplicated]);

  const labelisationTypeValue = datas.labelingTypes
    ? datas.labelingTypes[0].name
    : "";

  return (
    <form>
      <Grid
        container
        spacing={40}
        direction="row"
        justify="space-between"
        alignItems="stretch"
        className={classes.form}
      >
        <Grid item sm={6}>
          <Grid container direction="column" spacing={16}>
            <Grid item>
              <UiInput
                label="Nom du projet"
                name="name"
                autoFocus
                required
                disabled={typeof project.id !== "undefined"}
                onChange={handleChange}
                value={datas.name}
                onBlur={handleBlur}
              />
            </Grid>
            <Grid item>
              <UiSelect
                name="labelisationType"
                required
                disabled={typeof project.id !== "undefined"}
                value={labelisationTypeValue}
                label="Sélectionnez le type de labellisation"
                onChange={handleChange}
              >
                {LABELISATION_TYPES.map((f: any, index: number) => (
                  <option key={index} value={f.id}>
                    {f.text}
                  </option>
                ))}
              </UiSelect>
            </Grid>
            <Grid item>
              <UiSelect
                name="clientId"
                required
                disabled={typeof project.id !== "undefined"}
                value={datas.clientId}
                label="Client"
                onChange={handleChange}
              >
                <option value="" />
                {clients.map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </UiSelect>
            </Grid>
            <Grid item />
          </Grid>
        </Grid>
        <Grid item sm={6}>
          <Grid
            container
            direction="column"
            justify="space-between"
            style={{ height: "100%" }}
            component="form"
            onSubmit={(e: any) => e.preventDefault()}
          >
            {(labelisationTypeValue === "RECTANGLE_BOXING" ||
              labelisationTypeValue === "SEGMENTATION" ||
              labelisationTypeValue === "CLASSIF" ||
              labelisationTypeValue === "VIDEO") && (
              <Grid item>
                {/* MANUAL TAGS */}
                {!tagListMode && (
                  <>
                    <TagInput
                      appFetch={appFetch}
                      clientId={datas.clientId}
                      labelingType={labelisationTypeValue}
                      onAdd={addTagByName}
                      disabled={
                        typeof project.id === "undefined" &&
                        (!datas.clientId || !labelisationTypeValue)
                      }
                    />
                    <SwitchModeButton
                      labelisationTypeValue={labelisationTypeValue}
                      toggleTagListMode={toggleTagListMode}
                      classes={classes}
                      label="Utiliser une liste de tags"
                    />
                    {datas.labelingTypes &&
                      datas.labelingTypes[0].listTags.length > 0 && (
                        <Tags
                          datas={datas.labelingTypes[0].listTags}
                          originalTags={originalTags}
                          onRemoveTag={removeTag}
                          projectId={project.id}
                          setTags={setTags}
                          currentTag={tagDuplicated}
                        />
                      )}
                  </>
                )}

                {/* LIST TAGS */}
                {tagListMode && (
                  <>
                    <label className={classes.label}>
                      Choisissez une liste de tags :
                    </label>
                    <TagList
                      value={datas.tagListId}
                      onChange={handleChange}
                      tagLists={tagLists}
                      setTagsList={setTagsList}
                    />
                    <SwitchModeButton
                      labelisationTypeValue={labelisationTypeValue}
                      toggleTagListMode={toggleTagListMode}
                      classes={classes}
                      label="Tags manuels"
                    />
                    {datas.tagListId && (
                      <TagListSample tags={tagsList[datas.tagListId]} />
                    )}
                  </>
                )}
              </Grid>
            )}
            <Grid item>
              <Grid container direction="column">
                <Grid item>
                  <FormControl className={classes.formControl}>
                    <label className={classes.label}>
                      Choisissez votre source de données :
                    </label>
                    {datas.labelingTypes[0].name !== "VIDEO" ? (
                      <RadioGroup
                        aria-label="dataSource"
                        name="dataSource"
                        className={classes.sourceDonnees}
                        value={datas.dataSource}
                        onChange={handleChange}
                        row
                      >
                        <FormControlLabel
                          value="S3"
                          control={<Radio />}
                          disabled={typeof project.id !== "undefined"}
                          label="Dossier local"
                        />
                        <FormControlLabel
                          value="LABELS"
                          control={<Radio />}
                          disabled={typeof project.id !== "undefined"}
                          label="Labels"
                        />
                      </RadioGroup>
                    ) : (
                      <RadioGroup
                        aria-label="dataSource"
                        name="dataSource"
                        className={classes.sourceDonnees}
                        value={"S3"}
                        row
                      >
                        <FormControlLabel
                          value="S3"
                          control={<Radio />}
                          disabled
                          label="S3"
                        />
                      </RadioGroup>
                    )}
                  </FormControl>
                </Grid>
                {/* {datas.dataSource === "ES" && (
                  <Grid item>
                    <Grid container direction="column" spacing={8}>
                      <Grid item>
                        <UiSelect
                          name="indice"
                          required
                          value={datas.indice}
                          disabled={
                            datas.dataSource !== "ES" ||
                            typeof project.id !== "undefined"
                          }
                          onChange={handleChange}
                        >
                          <option value="">Sélectionnez l'index ES</option>
                          {indices.map((indice: any) => (
                            <option key={indice.name} value={indice.name}>
                              {indice.name}
                            </option>
                          ))}
                        </UiSelect>
                      </Grid>
                      {envs && (
                        <Grid item>
                          <UiSelect
                            name="environment"
                            required
                            value={datas.environment}
                            disabled={
                              datas.dataSource !== "ES" ||
                              typeof project.id !== "undefined"
                            }
                            onChange={handleChange}
                          >
                            <option value="">
                              {datas.indice !== ""
                                ? "Sélectionnez l'environment"
                                : ""}
                            </option>
                            {envs.map((env: any) => (
                              <option key={env} value={env}>
                                {env}
                              </option>
                            ))}
                          </UiSelect>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )} */}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item sm={12}>
          <Grid
            container
            direction="row"
            justify={error !== "" ? "space-between" : "flex-end"}
            spacing={8}
          >
            <Grid item sm={8}>
              {error !== "" && (
                <Slide direction="up" in>
                  <Typography className={classes.error}>{error}</Typography>
                </Slide>
              )}
            </Grid>
            <Grid item sm={4}>
              <UiButton
                style={{ marginTop: 8 }}
                fullWidth
                color="secondary"
                variant="contained"
                disabled={!isValidated()}
                onClick={submit}
              >
                Valider
              </UiButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

// @ts-ignore
export default withContext(withStyles(styles)(withRouter(UpdateProject)));
